:host {
  display: flex;
  a {
    text-decoration: none;
    display: inline-block;
  }
}

.link {
  letter-spacing: normal;
  display: inline-block;
  box-sizing: border-box;
  user-select: none;
  margin: 0;
  padding: 0;
  outline: none;
  overflow-wrap: anywhere;
  word-break: normal;
  hyphens: auto;
  background-color: var(--link-surface-default);

  &.prominent {
    font-family: var(--copytext-font-family-bold);
    line-height: 24px;
    font-weight: 400;
    letter-spacing: var(--letter-spacing-basic);
    font-size: 14px;
    text-transform: uppercase;
    .underlined {
      text-decoration-line: underline;
      -moz-text-decoration-line: underline;
      text-underline-offset: 6px;
      text-decoration-thickness: 2px;
    }
  }

  &.decent {
    font-family: var(--copytext-font-family);
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    .underlined {
      text-decoration-line: underline;
      -moz-text-decoration-line: underline;
      text-underline-offset: 2px;
      text-decoration-thickness: 1px;
    }
    .icon-wrapper {
      transform: translateY(1px);
    }
  }

  .content-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    &.no-anchor {
      display: inline flex;
    }
  }

  .icon-wrapper {
    display: flex;
    justify-content: center;
    text-decoration-line: none;
  }

  // Loading & disabled state
  &.disabled,
  &.loading {
    pointer-events: none;
    cursor: none;
  }

  // Hover state
  &:hover {
    cursor: pointer;
  }

  // Focus state
  a {
    outline-offset: 3px;
    outline: 2px solid transparent;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline-offset: 3px;
      outline: 2px solid var(--color-focus-outline);

      &.focus-gap-theme-color {
        box-shadow:
          0 0 0 3px var(--color-focus-gap),
          0 0 0 5px var(--color-focus-outline);
        outline: none;
      }
    }
  }

  &.color-inverted {
    &.no-anchor,
    a {
      &:focus-visible {
        outline-offset: 3px;
        outline: 2px solid var(--color-white);

        &.focus-gap-theme-color {
          box-shadow:
            0 0 0 3px var(--color-white),
            0 0 0 5px var(--color-white);
          outline: none;
        }
      }
    }
  }

  // State colors in lightmode
  &.prominent {
    color: var(--link-text-default);
    fill: var(--link-text-default);
    .icon-wrapper {
      patternlib-icon {
        color: var(--link-text-default) !important;
        fill: var(--link-text-default) !important;
      }
    }
  }

  &.decent {
    color: var(--link-text-decent-default);
    fill: var(--link-text-decent-default);
    .icon-wrapper {
      patternlib-icon {
        color: var(--link-text-decent-default) !important;
        fill: var(--link-text-decent-default) !important;
      }
    }
  }

  &.hovered,
  &:hover {
    color: var(--link-text-hover);
    fill: var(--link-text-hover);

    .underlined {
      text-decoration-color: var(--link-text-hover);
    }

    .link-content-wrapper-label {
      border-color: var(--link-text-hover);
    }

    .icon-wrapper {
      patternlib-icon {
        color: var(--link-text-hover) !important;
        fill: var(--link-text-hover) !important;
      }
    }
  }

  .active {
    color: var(--link-text-active);
    fill: var(--link-text-active);

    .underlined {
      text-decoration-color: var(--link-text-active);
    }

    .link-content-wrapper-label {
      border-color: var(--link-text-active);
    }

    .icon-wrapper {
      patternlib-icon {
        color: var(--link-text-active) !important;
        fill: var(--link-text-active) !important;
      }
    }
  }

  &.disabled,
  &.loading {
    color: var(--link-text-disabled);
    fill: var(--link-text-disabled);

    .underlined {
      text-decoration-color: var(--link-text-disabled);
    }

    .link-content-wrapper-label {
      border-color: var(--link-text-disabled);
    }

    .icon-wrapper {
      patternlib-icon {
        color: var(--link-text-disabled) !important;
        fill: var(--link-text-disabled) !important;
      }
    }
  }

  // State colors in lightmode + color-inverted
  &.color-inverted {
    &.prominent {
      color: var(--link-text-default-inverted);
      fill: var(--link-text-default-inverted);
      .icon-wrapper {
        patternlib-icon {
          color: var(--link-text-default-inverted) !important;
          fill: var(--link-text-default-inverted) !important;
        }
      }
    }

    &.decent {
      color: var(--link-text-default-inverted);
      fill: var(--link-text-default-inverted);
      .icon-wrapper {
        patternlib-icon {
          color: var(--link-text-default-inverted) !important;
          fill: var(--link-text-default-inverted) !important;
        }
      }
    }

    &.hovered,
    &:hover {
      color: var(--link-text-hover-inverted);
      fill: var(--link-text-hover-inverted);

      .underlined {
        text-decoration-color: var(--link-text-hover-inverted);
      }

      .link-content-wrapper-label {
        border-color: var(--link-text-hover);
        text-decoration-color: var(--link-text-hover-inverted);
      }

      .icon-wrapper {
        patternlib-icon {
          color: var(--link-text-hover-inverted) !important;
          fill: var(--link-text-hover-inverted) !important;
        }
      }
    }

    .active {
      color: var(--link-text-active-inverted);
      fill: var(--link-text-active-inverted);

      .underlined {
        text-decoration-color: var(--link-text-active-inverted);
      }

      .link-content-wrapper-label {
        border-color: var(--link-text-active);
        text-decoration-color: var(--link-text-active-inverted);
      }

      .icon-wrapper {
        patternlib-icon {
          color: var(--link-text-active-inverted) !important;
          fill: var(--link-text-active-inverted) !important;
        }
      }
    }

    &.disabled,
    &.loading {
      color: var(--link-text-disabled-inverted);
      fill: var(--link-text-disabled-inverted);

      .underlined {
        text-decoration-color: var(--link-text-disabled-inverted);
      }

      .link-content-wrapper-label {
        border-color: var(--link-text-disabled);
        text-decoration-color: var(--link-text-disabled-inverted);
      }

      .icon-wrapper {
        patternlib-icon {
          color: var(--link-text-disabled-inverted) !important;
          fill: var(--link-text-disabled-inverted) !important;
        }
      }
    }
  }
}
